import { createTheme } from "@mui/material/styles";
import {
  borderW,
  colors as c,
  fonts,
  lip,
  motion,
  radius,
  shadows,
  text,
} from "./tokens";

type Tone = { bg: string; fg: string; border: string; lip: string };

// The pressable tones shared by Button and Fab (DESIGN_SYSTEM.md → "The lip").
const tones: Record<string, Tone> = {
  primary: { bg: c.gold, fg: c.ink, border: c.ink, lip: c.goldShade },
  success: { bg: c.teal, fg: c.ink, border: c.ink, lip: c.tealShade },
  error: { bg: c.cherry, fg: c.ink, border: c.ink, lip: c.cherryShade },
  info: { bg: c.tealTint, fg: c.tealShade, border: c.tealShade, lip: c.teal },
  secondary: { bg: c.night, fg: c.nightInk, border: c.nightLip, lip: c.nightLip },
  night: { bg: c.night, fg: c.nightInk, border: c.nightLip, lip: c.nightLip },
  default: { bg: c.card, fg: c.ink, border: c.ink, lip: c.lineStrong },
};

const toneFor = (color?: string) => tones[color || "default"] || tones.default;

const pressable = (tone: Tone, lipSize = lip) => ({
  backgroundColor: tone.bg,
  color: tone.fg,
  border: `${borderW} solid ${tone.border}`,
  boxShadow: `0 ${lipSize} 0 ${tone.lip}`,
  transition: `transform ${motion.fast} ${motion.easeOut}, box-shadow ${motion.fast} ${motion.easeOut}, background-color ${motion.fast}`,
  "&:hover": {
    backgroundColor: tone.bg,
    boxShadow: `0 ${lipSize} 0 ${tone.lip}`,
    "@media (hover: hover)": {
      transform: "translateY(-1px)",
      boxShadow: `0 calc(${lipSize} + 1px) 0 ${tone.lip}`,
    },
  },
  "&:active": {
    transform: `translateY(${lipSize})`,
    boxShadow: `0 0 0 ${tone.lip}`,
  },
  "&.Mui-disabled": {
    backgroundColor: tone.bg,
    color: tone.fg,
    borderColor: tone.border,
    opacity: 0.5,
    boxShadow: "none",
    transform: "none",
  },
  "&.Mui-focusVisible": {
    outline: `3px solid ${c.teal}`,
    outlineOffset: "2px",
  },
});

const display = { fontFamily: fonts.display, color: c.ink };

export const materialTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: c.gold, light: c.goldTint, dark: c.goldShade, contrastText: c.ink },
    secondary: { main: c.night, light: c.nightSoft, dark: c.nightLip, contrastText: c.nightInk },
    success: { main: c.teal, light: c.tealTint, dark: c.tealShade, contrastText: c.ink },
    error: { main: c.cherryShade, light: c.cherryTint, dark: c.cherryShade, contrastText: c.card },
    warning: { main: c.gold, light: c.goldTint, dark: c.warn, contrastText: c.ink },
    info: { main: c.tealShade, light: c.tealTint, dark: c.tealShade, contrastText: c.card },
    background: { default: c.paper, paper: c.card },
    text: { primary: c.ink, secondary: c.inkSoft, disabled: c.inkFaint },
    divider: c.line,
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: fonts.body,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 800,
    h1: { ...display, fontSize: text.display, fontWeight: 700, lineHeight: 1.05 },
    h2: { ...display, fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.1 },
    h3: { ...display, fontSize: "2rem", fontWeight: 600, lineHeight: 1.15 },
    h4: { ...display, fontSize: text.xl, fontWeight: 600, lineHeight: 1.2 },
    h5: { ...display, fontSize: "1.4rem", fontWeight: 600, lineHeight: 1.25 },
    h6: { ...display, fontSize: text.lg, fontWeight: 600, lineHeight: 1.3 },
    subtitle1: { fontFamily: fonts.display, fontWeight: 600, fontSize: "1.125rem" },
    subtitle2: { fontWeight: 700, fontSize: text.sm },
    body1: { fontSize: text.base, lineHeight: 1.5 },
    body2: { fontSize: text.sm, lineHeight: 1.45 },
    caption: { fontSize: text.xs, fontWeight: 700 },
    button: { fontFamily: fonts.display, fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ ownerState }) => {
          const small = ownerState.size === "small";
          const base = {
            minHeight: small ? 36 : 48,
            padding: small ? "0 12px" : "0 20px",
            borderRadius: radius.md,
            fontSize: small ? text.sm : text.base,
            gap: 4,
          };
          if (ownerState.variant === "text") {
            return {
              ...base,
              color: c.inkSoft,
              "&:hover": { backgroundColor: c.cardSunken, color: c.ink },
            };
          }
          const tone =
            ownerState.variant === "outlined" || ownerState.color === "inherit"
              ? tones.default
              : toneFor(ownerState.color);
          return { ...base, ...pressable(tone, small ? "3px" : lip) };
        },
        startIcon: { marginRight: 4 },
        endIcon: { marginLeft: 4 },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...pressable(toneFor(ownerState.color === "inherit" ? "default" : ownerState.color)),
          zIndex: "auto",
          "& svg": { flexShrink: 0 },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: c.ink,
          borderRadius: radius.md,
          "&:hover": { backgroundColor: c.cardSunken },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          color: c.ink,
        },
        rounded: { borderRadius: radius.lg },
        outlined: { border: `${borderW} solid ${c.line}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `${borderW} solid ${c.line}`,
          borderRadius: radius.lg,
          boxShadow: shadows.card,
          overflow: "visible",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 20, "&:last-child": { paddingBottom: 20 } },
      },
    },
    MuiCardActions: {
      styleOverrides: { root: { padding: "0 20px 20px", gap: 8 } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: "0 20px 20px", gap: 8, "& > :not(style) ~ :not(style)": { marginLeft: 0 } },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `${borderW} solid ${c.ink}`,
          borderRadius: radius.lg,
          boxShadow: `0 ${lip} 0 ${c.lineStrong}, ${shadows.float}`,
          marginTop: 6,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: radius.lg },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: radius.sm,
          margin: "0 6px",
          "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: c.tealTint, color: c.tealShade },
          "&:hover": { backgroundColor: c.cardSunken },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: c.card,
          borderRadius: radius.md,
          transition: `box-shadow ${motion.fast}`,
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: borderW,
            borderColor: c.line,
            transition: `border-color ${motion.fast}`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: c.lineStrong },
          "&.Mui-focused": { boxShadow: `0 0 0 4px ${c.tealTint}` },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: borderW,
            borderColor: c.teal,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: c.cherry },
          "&.Mui-error.Mui-focused": { boxShadow: `0 0 0 4px ${c.cherryTint}` },
          "&.Mui-disabled": { backgroundColor: c.cardSunken },
        },
        input: { fontWeight: 600, padding: "13px 14px" },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          color: c.inkSoft,
          "&.Mui-focused": { color: c.tealShade },
          "&.Mui-error": { color: c.cherryShade },
        },
        outlined: {
          transform: "translate(14px, 13px) scale(1)",
          "&.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.8)" },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: { asterisk: { color: c.cherryShade } },
    },
    MuiSelect: {
      styleOverrides: { icon: { color: c.ink } },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: c.lineStrong,
          "&.Mui-checked": { color: c.tealShade },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontWeight: 600 } },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontFamily: fonts.display,
          fontWeight: 700,
          border: `${borderW} solid ${c.card}`,
          minWidth: 22,
          height: 22,
          borderRadius: radius.full,
        },
        colorError: { backgroundColor: c.cherry, color: c.ink },
        colorInfo: { backgroundColor: c.teal, color: c.ink },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: fonts.display,
          fontWeight: 600,
          borderRadius: radius.full,
          border: `${borderW} solid ${c.line}`,
          backgroundColor: c.cardSunken,
          color: c.ink,
        },
        outlined: { backgroundColor: c.card },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const tone = toneFor(ownerState.severity === "warning" ? "primary" : ownerState.severity || ownerState.color);
          const filled = ownerState.variant === "filled";
          return {
            borderRadius: radius.lg,
            border: `${borderW} solid ${filled ? c.ink : tone.border}`,
            backgroundColor: filled ? (ownerState.severity === "success" ? c.teal : tone.bg) : tone.bg,
            color: filled ? c.ink : tone.fg,
            fontWeight: 700,
            padding: "4px 14px",
            alignItems: "center",
            animation: `pop-in ${motion.slow} ${motion.easeBounce} both`,
            "& .MuiAlert-icon": { color: "inherit", opacity: 1, padding: "4px 0" },
            "& .MuiAlert-message": { padding: "4px 0" },
          };
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: c.night,
          color: c.nightInk,
          fontFamily: fonts.body,
          fontWeight: 700,
          fontSize: text.sm,
          borderRadius: radius.sm,
          padding: "4px 10px",
        },
        arrow: { color: c.night },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.invisible
            ? {}
            : {
                backgroundColor: "rgba(20, 16, 15, 0.55)",
                backdropFilter: "blur(3px)",
              },
      },
    },
    MuiCircularProgress: {
      defaultProps: { thickness: 5 },
    },
  },
});
